<template>
  <div
    class="cart-drawer critical-component-hide"
    :class="{ 'cart-drawer--full': fullCart }"
  >
    <div class="cart-drawer__scroll-element" js-overlay="scrollElement">
      <div class="cart-drawer__header">
        <div class="cart-drawer__header-top">
          <p
            class="cart-drawer__title text-mobile-headlines-h3 text-desktop-headlines-h3"
            v-text="$string('cart.title')"
          />

          <button
            class="overlay__close icon-button"
            type="button"
            @click.prevent="handleClose"
          >
            <span
              class="visually-hidden"
              v-text="$string('accessibility.close')"
            />

            <icon-close />
          </button>
        </div>

        <discount-code
          class="cart-drawer__discount-code"
          :is-active="overlay.isActive"
        />

        <cart-thresholds
          class="cart-drawer__cart-thresholds"
          :standard-shipping-rate="standardShippingRate"
          :requires-shipping="cart.requires_shipping"
          :excluded="{ property: 'gift_card', value: true }"
        />
      </div>

      <template v-if="fullCart">
        <div class="cart-drawer__line-items">
          <divider-line />

          <template
            v-for="lineItem of sortedLineItems"
            :key="getCartItemKey(lineItem)"
          >
            <line-item-freebie
              v-if="isFreebie(lineItem)"
              class="cart-drawer__line-item"
              :line-item="lineItem"
              location="cart-drawer"
            />
            <line-item
              v-else
              class="cart-drawer__line-item line-item"
              :line-item="lineItem"
              location="cart-drawer"
            />

            <divider-line />
          </template>
          <cart-freebies class="cart-summary__freebies" />
        </div>
      </template>

      <div v-else class="cart-drawer__empty-container">
        <div class="cart-drawer__empty-icon">
          <icon-cart />
        </div>

        <p
          class="cart-drawer__empty-title text-mobile-headlines-h4 text-desktop-headlines-h4"
          v-text="$string('cart.empty.title')"
        />

        <p
          class="cart-drawer__empty-subtitle text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
          v-text="$string('cart.empty.subtitle')"
        />

        <btn
          class="cart-drawer__empty-button"
          :label="$string('cart.empty.button_title')"
          modifiers="secondary block center"
          size="medium-large"
          :url="$variable('routes.allProductsCollection')"
        />
      </div>

      <div class="cart-drawer__footer">
        <cart-samples
          v-if="samples.length"
          class="cart-drawer__samples"
          :overlay="overlay"
          :samples="samples"
        />

        <div v-if="!fullCart && links.length" class="cart-drawer__collections">
          <p
            class="cart-drawer__collections-title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
            v-text="$string('cart.empty.collections_title')"
          />

          <featured-category-small-inner :links="links" />
        </div>
      </div>
    </div>

    <transition name="slide-up">
      <cart-summary v-if="fullCart" class="cart-drawer__summary" :limit="limit">
        <template #payments>
          <slot name="payments"></slot>
        </template>
      </cart-summary>
    </transition>
  </div>
</template>

<script>
/**
 * Vue: Cart drawer (cart-drawer)
 * -----------------------------------------------------------------------------
 * Displays contents of cart.
 *
 * @param {Number} [limit] - Line item limit to display notification panel at.
 * @param {Array} links - Empty state collection links.
 * @param {Object} overlay - Overlay API.
 * @param {Array} samples - Array of products to load in cart samples.
 * @param {Number} standardShippingRate - Standard shipping rate price.
 *
 */
import { mapState } from 'pinia';

import { formatTrackingItem } from '~/format/format-tracking-item';

import Btn from '~global/btn/btn';
import CartSamples from '~async/cart-samples/cart-samples';
import CartSummary from '~async/cart-summary/cart-summary';
import CartThresholds from '~async/cart-thresholds/cart-thresholds';
import DiscountCode from '~async/cart-drawer/discount-code';
import DividerLine from '~async/utils/divider-line/divider-line';
import FeaturedCategorySmallInner from '~async/featured-category-small/featured-category-small-inner';
import LineItem from '~async/line-item/line-item';
import LineItemFreebie from '~async/line-item/line-item-freebie.vue';
import CartFreebies from '~async/cart-freebies/cart-freebies';

import IconCart from '~icons/general/cart.svg';
import IconClose from '~icons/directional-navigation/close.svg';
import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

export default {
  name: 'CartDrawer',

  components: {
    Btn,
    CartFreebies,
    CartSamples,
    CartSummary,
    CartThresholds,
    DiscountCode,
    DividerLine,
    FeaturedCategorySmallInner,
    IconCart,
    IconClose,
    LineItem,
    LineItemFreebie,
  },

  props: {
    limit: {
      type: Number,
      default: 20,
    },
    links: {
      type: Array,
      default: () => [],
      required: true,
    },
    overlay: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    samples: {
      type: Array,
      default: () => [],
      required: true,
    },
    standardShippingRate: {
      type: Number,
      default: 0,
      required: true,
    },
  },

  data() {
    return {};
  },

  computed: {
    ...mapState(useCartStore, {
      cart: 'response',
      cartStatus: 'status',
    }),
    ...mapState(useDiscountCampaignsStore, {
      enteredCode: 'enteredCode',
    }),

    sortedLineItems() {
      const cartItems = this.cart.items;
      cartItems.sort((a, b) => {
        const isAFreebie = this.isFreebie(a);
        const isBFreebie = this.isFreebie(b);
        if (isAFreebie !== isBFreebie) return isAFreebie < isBFreebie ? -1 : 1;

        if (isAFreebie && !isBFreebie) {
          return -1;
        }
        if (!isAFreebie && isBFreebie) {
          return 1;
        }
        return 0;
      });
      return cartItems;
    },

    /**
     * Compute cart items.
     * @returns {Array}
     */
    cartItems() {
      return this.cart.items;
    },

    /**
     * Compute full cart state.
     * @returns {Boolean}
     */
    fullCart() {
      return this.cart.item_count > 0 || this.cartStatus.label === 'adding';
    },
  },

  watch: {
    /**
     * Watch cart items to update focus trap.
     */
    'cart.items'() {
      if (!this.overlay.isActive) {
        return;
      }

      this.overlay.refresh({ currentElement: document.activeElement });
    },

    /**
     * Watch when cart is opened/closed.
     */
    'overlay.isActive'(isActive) {
      if (isActive) {
        this.handleCartOpenTracking();
      }
    },
  },

  mounted() {
    if (!this.overlay.isActive) {
      return;
    }
    // Track cart open event
    this.handleCartOpenTracking();

    this.overlay.refresh();
  },

  methods: {
    /**
     * Get cart item key.
     * @param {Object} item - Line item.
     * @param {Number} index - Line item index.
     * @returns {String}
     */
    getCartItemKey(item, index) {
      if (!item) {
        return `cart-line-item-loading-${index}`;
      }

      return `cart-line-item-${item.key}`;
    },

    /**
     * Compute if freebie.
     * @returns {Boolean}
     */
    isFreebie(lineItem) {
      return Object.keys(lineItem.properties).includes(
        this.$variable('discount.freebies.property'),
      );
    },

    /**
     * Close cart drawer.
     */
    handleClose() {
      this.overlay.close();
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      this.overlay.open({
        component: 'cart-drawer',
        ignoreDismissed: true,
      });
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.overlay.close();
    },

    /**
     * Handle cart open tracking
     */
    handleCartOpenTracking() {
      cnvs.EventBus.emit('dataLayer:view_cart', {
        event: 'view_cart',
        currency: this.cart.currency,
        items: this.cart.items.map((item) => {
          const lineLevelDiscountAllocations =
            item.line_level_discount_allocations;
          const discountCode = this.enteredCode;
          const percentageDiscount = lineLevelDiscountAllocations.reduce(
            (acc, discount) => {
              if (discount.discount_application.title === discountCode) {
                return discount.discount_application.value;
              }
              return acc;
            },
            0,
          );
          const discountObject = {
            percentageDiscount,
            discountCode,
          };
          const deliveryTime = this.$string('product.delivery_time', {
            replace: { time: this.$variable('product.deliveryTime') },
          });
          const currencyCode = this.$variable('store.currency');

          return formatTrackingItem({
            variant: item,
            template: 'Cart Drawer',
            discountObject,
            deliveryTime,
            currencyCode,
            quantity: item.quantity,
          });
        }),
      });
    },
  },
};
</script>

<style lang="scss">
@import './cart-drawer';
</style>
